// ImportForm.tsx
import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { Box, Button, Paper, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid, GridColDef, GridRowSelectionModel, type GridRenderCellParams, type GridRowIdGetter } from '@mui/x-data-grid';
import { api } from "../../../api/plateApi";
import { useFetch } from "../../../hooks/useFetch2";
import { useTranslation } from 'react-i18next';
// import { FormModal } from '../../modal/FormModal.tsx';
import { Origin } from '../../../types/origin.ts';
import { ImportRecord, Plate } from '../../../types/plate.ts';
// import { useCustomer } from '../../../contexts/CustomerContext.tsx';
// import { Group } from '../../../types/group.ts';
import { GroupSelect } from '../groups/GroupSelect2.tsx';
import { getErrorMessage } from '../../../tools/error.ts';
import LocalizedGrid from '../../localizedGrid/LocalizedGrid';
import i18n from '../../../i18n';
import { useAuthStore } from '../../../store/useAuthStore3.ts';

interface ImportFormProps {
    origin: Origin | null;
    onClose: () => void;
}

export const ImportForm: React.FC<ImportFormProps> = ({origin, onClose}) => {
    const {t} = useTranslation();
    const { customerId } = useAuthStore();
    const { data, setData, error, update, loading } = useFetch<ImportRecord[]>(
        api.importPlates, [], origin?.entity_id);
    const [selectionModel, setSelectionModel] = React.useState<number[]>([]);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [groupId, setGroupId] = React.useState<number | null>(null);

    if (loading) {
        return <p>{t('ImportForm.loading')}</p>;
    }

    const columns: GridColDef[] = [
        { field: "id", headerName: "ID", type: "number", flex: 1, width: 50, },
        { field: "registration_number", headerName: t('ImportForm.registration_number'), type: "number", flex: 1, width: 50, },
        { field: "car_brand", headerName: t('ImportForm.car_brand'), type: "string", flex: 1, width: 50, },
        { field: "car_owner", headerName: t('ImportForm.car_owner'), type: "string", flex: 1, width: 50, },
    ];
    
    const handleStoreToDB = async () => {
        setSubmitError(null);
        try {
            const data1 = data
                .filter((row) => selectionModel.includes(row.id))
                .map((row) => ({
                    ...row,
                    customer_id: customerId || -1,
                    group_id: groupId || -1,
                }));
            await api.postPlates(data1 as Plate[]);
            onClose();
        } catch (error) {
          console.error("Save failed:", error);
          const errorMessage = getErrorMessage(
            error, 
            t('ImportForm.Failed_to_save_plates')
          );
          setSubmitError(errorMessage);
        }
    }

    const handleSelectionChange = (newSelectionModel: GridRowSelectionModel) => {
        setSelectionModel(newSelectionModel as number[]);
    };

    return (
        <>
            <Typography variant="h4">{t('ImportForm.title')}</Typography>
            
            {submitError && (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {submitError}
            </Typography>
            )}
            
 
            {/* <DataGrid<ImportRecord> */}
            <LocalizedGrid
                lang={i18n.language as any}
                rows={data}
                columns={columns} 
                getRowId={(row) => row.id} 
                sx={{ maxWidth: '1200px' }}
                loading={loading}
                rowSelectionModel={selectionModel}
                onRowSelectionModelChange={handleSelectionChange}
                checkboxSelection
            />

            <Stack direction="row" spacing={2} sx={{ mb: 2, justifyContent: 'flex-end'}}>
                <Box width={200}>
                <GroupSelect
                    selectedGroupId={groupId}
                    onSelect={(gid) => setGroupId(gid)}
                />
                </Box>
                <Button 
                    variant="outlined" 
                    onClick={() => onClose()}
                >
                    {t('ImportForm.cancel')}
                </Button>
                <Button 
                    variant="contained" 
                    onClick={() => handleStoreToDB()}
                    disabled={selectionModel.length === 0 || !groupId}
                >
                    {t('ImportForm.StoreToDB')}
                </Button>
            </Stack>
        </>
    );
}